import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormContext } from './context'

import styles from './index.module.css'

import Checkbox from './Checkbox'
import CheckboxToggleButton from './CheckboxToggleButton'
import Label from './Label'
import Input from './Input'
import ClearButton from './ClearButton'
import SubmitButton from './SubmitButton'
import Fieldset from './Fieldset'
import Legend from './Legend'
import Separator from './Separator'
import TextArea from './TextArea'
import validations, { type Rule } from './Validations'
import Collapsible from './Collapsible'
import Skeleton from './Skeleton'
import Slider from './Slider'
import { cn, type UtilityClassNames } from '@s4l/utility-css'

export type FormValueTypes = string | boolean | number | string[] | number[]
export type FormValues<T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>> =
  T

export type ItemMetadata = { initialized: boolean; hasValue: boolean }
export type FormMetadata = { submitted: boolean; formItems: Record<string, ItemMetadata> }

interface IFormProps<T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>> {
  defaultValue?: FormValues<T>
  onSubmit?: (form: FormValues<T>) => void
  onChange?: (form: FormValues<T>) => void
  children: React.ReactNode
  debounceOnChange?: number
  className?: UtilityClassNames
}

const Form = <T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>>({
  onSubmit,
  onChange,
  defaultValue,
  children,
  debounceOnChange = 0,
  className,
}: IFormProps<T>) => {
  const [form, setForm] = useState<FormValues<T>>(defaultValue ?? ({} as FormValues<T>))
  const [formValidations, setFormValidations] = useState<Record<string, Array<ReturnType<Rule>>>>(
    {}
  )
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formMetaData, setFormMetaData] = useState<FormMetadata>({
    submitted: false,
    formItems: {},
  })

  const isUserChangeRef = useRef(false)

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const setFormValue = useCallback((key: string, value: FormValueTypes, isUserAction: boolean) => {
    isUserChangeRef.current = isUserAction
    setForm(prev => ({ ...prev, [key]: value }) as FormValues<T>)
  }, [])

  const setFormArrayValue = useCallback(
    (key: string, value: number | string | boolean | undefined, isUserAction: boolean) => {
      isUserChangeRef.current = isUserAction

      setForm(prev => {
        const prevValue = prev[key]
        if (Array.isArray(prevValue)) {
          return {
            ...prev,
            [key]: [...prevValue, value as string].filter(Boolean),
          } as FormValues<T>
        }
        return { ...prev, [key]: [value as string].filter(Boolean) } as FormValues<T>
      })
    },
    []
  )

  const removeFormArrayValue = useCallback(
    (key: string, value: number | string | boolean, isUserAction: boolean) => {
      isUserChangeRef.current = isUserAction

      setForm(prev => {
        const prevValue = prev[key]
        if (Array.isArray(prevValue)) {
          const filtered = (prevValue as string[]).filter(item => item !== value)
          return { ...prev, [key]: filtered } as FormValues<T>
        }
        return { ...prev, [key]: [] } as FormValues<T>
      })
    },
    []
  )

  const deleteFormValue = useCallback((key: string, isUserAction: boolean) => {
    isUserChangeRef.current = isUserAction
    setForm(prev => {
      const { [key]: _, ...rest } = prev

      return rest as FormValues<T>
    })

    setErrors(prev => {
      const { [key]: _, ...rest } = prev

      return rest
    })
  }, [])

  const setFormValidation = useCallback(
    (key: string, inputValidations: Array<ReturnType<Rule>>) => {
      setFormValidations(prev => ({ ...prev, [key]: inputValidations }))
    },
    []
  )

  const validateForm = () => {
    const { isValid, errors: newErrors } = Object.entries(formValidations).reduce(
      (acc, [key, inputValidations]) => {
        const value = form[key]

        const failedResult = inputValidations
          .map(validation => validation(value))
          .find(result => !result.isValid)

        if (failedResult && failedResult.isValid === false) {
          acc.errors[key] = failedResult.message
          acc.isValid = false
        }

        return acc
      },
      { isValid: true, errors: {} as Record<string, string> }
    )

    setErrors(newErrors)

    return isValid
  }

  const setFormMetaDataInner = useCallback((metaData: Partial<FormMetadata>) => {
    setFormMetaData(prev => ({ ...prev, ...metaData }))
  }, [])

  const setFormItemMetaData = useCallback((key: string, metaData: Partial<ItemMetadata>) => {
    setFormMetaData(prev => ({
      ...prev,
      formItems: {
        ...prev.formItems,
        [key]: {
          ...(prev.formItems[key] || {}),
          ...metaData,
        },
      },
    }))
  }, [])

  useEffect(() => {
    if (!isUserChangeRef.current) return undefined

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (formMetaData.submitted) {
        validateForm()
      }

      onChange?.(form)
    }, debounceOnChange)

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [form]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setErrors({})
    setFormMetaData(prev => ({ ...prev, submitted: true }))

    if (!validateForm()) return
    onSubmit?.(form)
  }

  const contextValue = useMemo(
    () => ({
      form,
      errors,
      formMetaData,
      setFormValue,
      setFormArrayValue,
      removeFormArrayValue,
      deleteFormValue,
      setFormValidation,
      setFormMetaData: setFormMetaDataInner,
      setFormItemMetaData,
      hasFormInitialized: Object.values(formMetaData.formItems).every(item => item.initialized),
    }),
    [
      form,
      errors,
      formMetaData,
      setFormValue,
      setFormArrayValue,
      removeFormArrayValue,
      deleteFormValue,
      setFormValidation,
      setFormMetaDataInner,
      setFormItemMetaData,
    ]
  )

  return (
    <FormContext.Provider value={contextValue}>
      <form onSubmit={handleSubmit} className={cn(styles.form, className)}>
        {children}
      </form>
    </FormContext.Provider>
  )
}

Form.Checkbox = Checkbox
Form.CheckboxToggleButton = CheckboxToggleButton
Form.Label = Label
Form.Input = Input
Form.ClearButton = ClearButton
Form.SubmitButton = SubmitButton
Form.Fieldset = Fieldset
Form.Legend = Legend
Form.Separator = Separator
Form.TextArea = TextArea
Form.Collapsible = Collapsible
Form.Skeleton = Skeleton
Form.Slider = Slider

Form.Validations = validations

Form.displayName = 'Form'
export default Form
